<!-- Form for a product or service that allows interaction with the shopping cart -->
<template>
	<div
		class="flex overflow-hidden"
		:class="twoColumns ? 'flex-row gap-6' : 'flex-col-reverse'"
	>
		<!-- Content -->
		<div :class="twoColumns ? 'w-1/2' : 'w-full'">
			<!-- name -->
			<div class="text-secondary-900 mx-1 mt-5 font-serif text-4xl italic">
				{{ product?.name }}
			</div>

			<!-- price -->
			<div
				v-if="product && product.pricingType !== 'Custom'"
				class="text-primary-700 mt-6 flex items-center gap-2 text-[22px] font-bold"
			>
				{{ formatPrice(product.fixedPrice) }}
				<ShieldCheckIcon
					class="text-accent-600 size-6"
					v-if="product?.isGuaranteed"
				/>
			</div>

			<!-- description -->
			<div class="text-secondary-900 my-4 text-sm">
				{{ product?.description }}
			</div>

			<!-- Input for adding to plans -->
			<div v-if="product?.category === PbProductType.Oap">
				<InputCheckbox
					v-for="plan in plans"
					:key="plan.id"
					class="my-4 w-full"
					:plan="plan"
					:product="product"
					:model-value="plan.oapIncluded"
					@update:model-value="plan.oapIncluded = !!$event"
				/>
			</div>
			<div v-else-if="product?.category === PbProductType.Everplans">
				<InputCheckbox
					v-for="plan in everplansPlans"
					:key="plan.id"
					class="my-4 w-full"
					:plan="plan"
					:product="product"
					:model-value="plan.everplansIncluded"
					@update:model-value="plan.everplansIncluded = !!$event"
				/>
			</div>

			<div v-else-if="product?.pricingType === 'Custom'">
				<TabsPlans v-model="activePlanIndex" />
				<InputCardsPriceOptions
					:product="product"
					:active-plan-index="activePlanIndex"
					:key="activePlanIndex"
				/>
			</div>
			<div v-else>
				<InputQuantity
					v-for="plan in plans"
					:key="plan.id"
					class="my-4 w-full"
					:plan="plan"
					:product="product"
					:hasCounter="hasCounter"
				/>
			</div>
		</div>

		<!-- Image -->
		<div :class="twoColumns ? 'w-1/2' : 'w-full'">
			<BlobImage
				v-if="product?.productImageUrl"
				class="max-h-screen w-full rounded-xs object-cover"
				:src="product?.productImageUrl"
				:alt="product?.name"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import BlobImage from '../common/BlobImage.vue'
import TabsPlans from '../presentation/TabsPlans.vue'
import InputCardsPriceOptions from './InputCardsPriceOptions.vue'
import InputCheckbox from './InputCheckbox.vue'
import InputQuantity from './InputQuantity.vue'
import type { PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import { PbProductType } from '@/enums/PbProductType'
import { useMerchandise } from '@/mixins/merchandise'
import { useCartStore } from '@/stores/cart'
import { useSessionStore } from '@/stores/session'
import ShieldCheckIcon from '@heroicons/vue/24/solid/ShieldCheckIcon'
import { type PropType, computed, ref } from 'vue'

const { formatPrice } = useMerchandise()
const session = useSessionStore()
const cartStore = useCartStore()

const plans = computed(() => session.activePlans)
const activePlanIndex = ref(0)

// Plans whose package already includes premium Everplans cannot toggle it here
const everplansPlans = computed(() =>
	(plans.value ?? []).filter(
		(plan) => !cartStore.cart[plan.id]?.includesFreeEverplansPremium
	)
)

const props = defineProps({
	product: Object as PropType<PbLineItemProductAndPricingOptions>,
	hasCounter: Boolean,
	twoColumns: Boolean,
})
</script>
